import { useEffect, useState, useCallback } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Helmet } from "fusion-plugin-react-helmet-async";
import { assetUrl } from "fusion-core";

import LabelInput from "../../../shared/components/labelInput";
import Pair from "../../../assets/images/pair";
import DriverNameFields from "../../../shared/components/driverNameFields";
import LicenseModal from "../../../shared/components/licenseModal";

import { TAXI_ENABLE_DATEPASSED } from "../../../config/config";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import { handleNext } from "../../../shared/utils/navigationUtils";
import QuoteStep from "../enums/QuoteStep";
import Tq1Mapper from "../mappers/tq1Mapper";
import { fetchTitlesService } from "../../../shared/services/simpleListItemsService";
import NewDatePicker from "../../../shared/components/newDatePicker";
import { generateDLN } from "../../../shared/utils/generateDLN";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import { scrollToTop } from "../../../shared/utils/scrollUtils";

const Tq1 = () => {
  const {
    register,
    formState: { errors, isValid },
    watch,
    control,
    setValue,
    trigger,
    handleSubmit,
  } = useForm({
    defaultValues: Tq1Mapper.get(),
  });

  // State
  const [titles, setTitles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  // Watch values
  const watchFields = ["lastName", "datePassedTest"];
  const gender = watch("title.gender");
  const [lastName, datePassedTest] = watch(watchFields);

  const tooltips = assetUrl(
    "../../../../node_modules/react-tooltip/dist/react-tooltip.css"
  );

  const DOB = useWatch({ control, name: "dateOfBirth" });

  useEffect(() => {
    if (DOB && lastName && gender) {
      const newDLN = generateDLN(lastName, DOB, gender);
      setValue("license", newDLN);
    }
  }, [DOB, lastName, gender, setValue]);

  const MAGIC_DATE_PASSED_TEST = "01/01/2008";

  const convertArrayFormat = useCallback(
    (array) =>
      array.map((item) => ({
        value: item.listItemId,
        label: item.listItemDescription,
        gender: item.listItemExtraInfoValue === "M" ? "Male" : "Female",
      })),
    []
  );

  const sortTitles = useCallback(
    (titles) =>
      titles.sort((a, b) => {
        const aDescription = a.listItemDescription;
        const bDescription = b.listItemDescription;
        if (
          (aDescription === "Mr" || aDescription === "Mrs") &&
          !(bDescription === "Mr" || bDescription === "Mrs")
        )
          return -1;
        if (
          !(aDescription === "Mr" || aDescription === "Mrs") &&
          (bDescription === "Mr" || bDescription === "Mrs")
        )
          return 1;
        if (aDescription.startsWith("M") && !bDescription.startsWith("M"))
          return -1;
        if (!aDescription.startsWith("M") && bDescription.startsWith("M"))
          return 1;
        return 0;
      }),
    []
  );

  // // Event Handlers

  // Effects
  useEffect(() => {
    let isMounted = true;
    const fetchAndSetTitles = async () => {
      try {
        setIsLoading(true);
        const titles = await fetchTitlesService();
        const sortedTitles = sortTitles([...titles]);
        if (isMounted) setTitles(sortedTitles);
      } catch (error) {
        // Optionally show a user-friendly error message here
        console.error("Error updating titles:", error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    fetchAndSetTitles();
    return () => {
      isMounted = false;
    };
  }, [sortTitles]);

  useEffect(() => {
    if (datePassedTest) trigger("datePassedTest");
  }, [datePassedTest, trigger]);

  useEffect(() => {
    const datePassed = watch("datePassedTest");
    if (datePassed === MAGIC_DATE_PASSED_TEST) {
      setValue("datePassedTest", null);
    }
  }, [watch, setValue]);

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ1_ENTERED);
  }, []);

  const onSubmit = (data) => {
    if (isValid) {
      TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ1_NEXT_CLICKED);
    }
    Tq1Mapper.set(data);
    handleNext(QuoteStep.TQ2);
  };

  // Render
  return (
    <>
      <Helmet>
        <link rel="stylesheet" href={tooltips} />
      </Helmet>
      <QuoteFormWrapper>
        <Pair className="stepOneImage" />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="fields tooltip-container margin-top-20">
            <LabelInput
              id="title"
              label="Title"
              type="select"
              placeholder="e.g Mrs"
              options={convertArrayFormat(titles)}
              register={register("title", { required: "Required" })}
              errors={errors?.title}
              watch={watch("title")}
              control={control}
              isLoading={isLoading}
              trigger={trigger}
            />

            <DriverNameFields {...{ register, errors, watch, trigger }} />
            <NewDatePicker
              name="dateOfBirth"
              label="Date of birth"
              errors={errors.dateOfBirth}
              setValue={setValue}
              trigger={trigger}
              control={control}
              rules={{
                required: "Date is required",
              }}
              watch={watch}
              maxDate={new Date()}
            />

            {/* License Input Section */}
            <label className="labelName">Driving licence number</label>
            <p className="licPara">
              We require your Driving Licence Number ("DLN") to provide this to
              the DVLA to confirm your licence status, entitlement and
              restriction information and endorsement/conviction data.
            </p>
            <div className="twoDivs">
              <LabelInput
                id="license"
                type="text"
                placeholder="xxxxxxxxxxx"
                disabled={true}
                maxLength={11}
                register={register("license", {
                  required: "Required",
                  pattern: {
                    value: /^[a-zA-Z0-9]{11}$/,
                    message: "Invalid format (xxxxxxxxxxx)",
                  },
                })}
                errors={errors?.license}
                trigger={trigger}
                watch={watch("license")}
                classNamelic="licFieldAuto"
                classTick="TickStyle"
                classLickField="lickFieldClass"
              />
              <LabelInput
                id="licenseUser"
                type="text"
                placeholder="xxxxx"
                maxLength={5}
                register={register("licenseUser", {
                  required: "Required",
                  pattern: {
                    value: /^[a-zA-Z0-9]{5}$/,
                    message: "Invalid format (xxxxx)",
                  },
                })}
                errors={errors?.licenseUser}
                trigger={trigger}
                watch={watch("licenseUser")}
                classNamelic="licFieldAuto"
                classTick="TickStyleUserLic"
                classLickField="lickFieldUserClass"
              />

              <button
                onClick={() => setIsOpen(true)}
                className="FindButton"
                type="button"
              >
                Need help?
              </button>
            </div>

            <LicenseModal
              isOpen={isOpen}
              closeModal={useCallback(() => setIsOpen(false), [])}
            />

            {TAXI_ENABLE_DATEPASSED && (
              <NewDatePicker
                name="datePassedTest"
                label="Date Passed Test"
                errors={errors.datePassedTest}
                trigger={trigger}
                setValue={setValue}
                control={control}
                rules={{
                  required: "Date is required",
                }}
                watch={watch}
                maxDate={new Date()}
              />
            )}

            <button
              type="submit"
              className={` margin-top-15
              ${!isValid ? "buttonGreyOut" : "getStartedButton"}`}
              onClick={scrollToTop}
            >
              Get Started
            </button>
          </div>
        </form>
      </QuoteFormWrapper>
    </>
  );
};

export default Tq1;
